import { memo } from "react";
import { Button, Select } from "@arco-design/web-react";
import { ChevronLeft, ChevronRight, Database, LoaderCircle, Plus } from "lucide-react";

import type { MySqlQueryDisplayColumn, MySqlQueryListItem, PaginationItem, SelectOption } from "../types";

type MySqlQueryResultTableProps = {
  columns: MySqlQueryDisplayColumn<MySqlQueryListItem>[];
  items: MySqlQueryListItem[];
  loading: boolean;
  pageSizeSelectOptions: SelectOption[];
  onOpenCreateModal: () => void;
  onPageChange: (nextPage: number) => void;
  onPageSizeChange: (nextPageSize: number) => void;
  page: number;
  pageSize: number;
  paginationItems: PaginationItem[];
  total: number;
  totalPages: number;
};

/** 生成表格单元格对齐类名。 */
function getTableAlignClassName(align: MySqlQueryDisplayColumn<MySqlQueryListItem>["align"]): string | undefined {
  if (align === "center") {
    return "is-align-center";
  }

  if (align === "right") {
    return "is-align-right";
  }

  return undefined;
}

/** MySQL 查询列表结果卡片。 */
export const MySqlQueryResultTable = memo(function MySqlQueryResultTable({
  columns,
  items,
  loading,
  pageSizeSelectOptions,
  onOpenCreateModal,
  onPageChange,
  onPageSizeChange,
  page,
  pageSize,
  paginationItems,
  total,
  totalPages,
}: MySqlQueryResultTableProps) {
  return (
    <section className="mysql-query-results-card" aria-busy={loading}>
      <div data-testid="mysql-query-results-header" className="mysql-query-results-header">
        <h3 className="mysql-query-results-title">查询结果</h3>
        <Button shape="round" icon={<Plus size={16} strokeWidth={2.2} />} onClick={onOpenCreateModal}>
          新增记录
        </Button>
      </div>

      <div className="mysql-query-table-stage">
        <div className="mysql-query-table-scroll" data-testid="mysql-query-table-wrapper">
          <table className="mysql-query-data-table" style={{ width: "max-content", minWidth: "100%" }}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={getTableAlignClassName(column.align)} style={{ maxWidth: "50vw" }}>
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="mysql-query-empty-cell" aria-label="正在加载列表数据">
                    <div className="mysql-query-table-state is-loading" aria-live="polite">
                      <div className="mysql-query-table-state-illustration">
                        <LoaderCircle className="mysql-query-table-state-icon" size={20} strokeWidth={2.25} />
                      </div>
                      <div className="mysql-query-table-state-title">正在加载列表数据</div>
                      <div className="mysql-query-table-state-description">
                        请稍候，结果会显示在表格中。
                      </div>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="mysql-query-empty-cell">
                    <div className="mysql-query-table-state">
                      <div className="mysql-query-table-state-illustration">
                        <Database className="mysql-query-table-state-icon" size={20} strokeWidth={1.9} />
                      </div>
                      <div className="mysql-query-table-state-title">当前筛选条件下暂无数据</div>
                      <div className="mysql-query-table-state-description">
                        可以调整筛选条件或切换页大小后重新查看。
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    {columns.map((column) => (
                      <td key={column.key} className={getTableAlignClassName(column.align)} style={{ maxWidth: "50vw" }}>
                        <div className="mysql-query-cell-content">{column.render(item)}</div>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mysql-query-pagination-bar">
        <div className="mysql-query-pagination-actions-row" data-testid="mysql-query-pagination-actions-row">
          <div className="mysql-query-pagination-actions">
            <button
              type="button"
              className="mysql-query-page-button is-nav"
              aria-label="上一页"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || loading}
            >
              <ChevronLeft size={16} strokeWidth={2.2} />
            </button>

            {paginationItems.map((paginationItem) =>
              typeof paginationItem === "number" ? (
                <button
                  key={paginationItem}
                  type="button"
                  className={
                    paginationItem === page
                      ? "mysql-query-page-button is-page-number is-active"
                      : "mysql-query-page-button is-page-number"
                  }
                  onClick={() => onPageChange(paginationItem)}
                  disabled={loading}
                >
                  {paginationItem}
                </button>
              ) : (
                <span key={paginationItem} className="mysql-query-page-ellipsis" aria-hidden="true">
                  ...
                </span>
              ),
            )}

            <button
              type="button"
              className="mysql-query-page-button is-nav"
              aria-label="下一页"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || loading}
            >
              <ChevronRight size={16} strokeWidth={2.2} />
            </button>
          </div>
        </div>

        <div className="mysql-query-pagination-meta-row" data-testid="mysql-query-pagination-meta-row">
          <div className="mysql-query-pagination-total">共 {total} 条</div>
          <div className="mysql-query-page-size-control">
            <Select
              size="small"
              autoWidth
              className="mysql-query-page-size-select"
              data-testid="mysql-query-page-size-select"
              aria-label="每页显示条数"
              value={pageSize}
              options={pageSizeSelectOptions}
              onChange={(value) => onPageSizeChange(Number(value))}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </section>
  );
});
